import { useState } from 'react';
import Icon from 'components/AppIcon';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';

const BankDetailsDisplay = ({
  isSettingAsPrimaryBankDetail,
  handleSetAsPrimaryBankDetail,
  bankDetailsData = [],
  onEdit,
  onAddNew,
  onDelete,
}) => {
  const [expandedId, setExpandedId] = useState(null);
  const [showFullAccountNumber, setShowFullAccountNumber] = useState({});

  const hasMultipleAccounts = bankDetailsData.length > 1;

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const toggleAccountNumberVisibility = (id) => {
    setShowFullAccountNumber((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="space-y-6 p-4">
      <div className="space-y-4">
        {bankDetailsData.map((account) => {
          const isExpanded = expandedId === account.id;
          const showFullNumber = showFullAccountNumber[account.id];

          return (
            <div
              key={account.id}
              className={`border rounded-xl p-4 transition-all ${
                account.is_primary
                  ? 'border-primary-200 bg-primary-50'
                  : 'border-gray-200 hover:shadow-md'
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      account.is_primary
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <Icon name="Landmark" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{account.bank_name}</h3>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-gray-500">
                        {showFullNumber
                          ? account.account_number
                          : account.account_number.replace(/(?<=^\d{4})\d+(?=\d{4}$)/, '*****')}
                      </p>
                      <button
                        onClick={() => toggleAccountNumberVisibility(account.id)}
                        className="text-xs outline-none focus:outline-none text-primary-600 hover:text-primary-800 flex items-center gap-1"
                      >
                        <Icon name={showFullNumber ? 'EyeOff' : 'Eye'} size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {account.is_primary && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center gap-1">
                      <Icon name="CheckCircle" size={12} />
                      Primary
                    </span>
                  )}
                  <button
                    onClick={() => toggleExpand(account.id)}
                    className="p-1 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                  >
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} />
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Account Holder</p>
                      <p className="font-medium">{account.account_holder}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Account Type</p>
                      <p className="font-medium capitalize">{account.account_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">IFSC Code</p>
                      <p className="font-mono font-medium">{account.ifsc_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Branch</p>
                      <p className="font-medium">{account.branch_name}</p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    {!account.is_primary && hasMultipleAccounts && (
                      <button
                        disabled={isSettingAsPrimaryBankDetail}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSetAsPrimaryBankDetail(account.id);
                        }}
                        className="px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center gap-1"
                      >
                        <Icon name="Star" size={14} />
                        {isSettingAsPrimaryBankDetail ? <ThreeDotLoading /> : 'Set as Primary'}
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(account.id)}
                      className="px-4 bg-red-100 text-red-600 hover:bg-red-150 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Icon name="Trash" size={14} />
                    </button>
                    <button
                      onClick={() => onEdit(account)}
                      className="px-4 py-2 text-sm bg-green-100 font-medium text-gray-600 hover:bg-gray-150 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <Icon name="Edit2" size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {bankDetailsData.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <Icon name="Landmark" size={24} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">No bank accounts added</h3>
          <p className="text-gray-500 mt-1">Add your first bank account to receive payments</p>
          <button
            onClick={onAddNew}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Add Bank Account
          </button>
        </div>
      )}
    </div>
  );
};

export default BankDetailsDisplay;
